import { Button } from "@admin/components/atoms/Button";
import { ErrorMessage } from "@admin/components/atoms/ErrorMessage";
import { Input } from "@admin/components/atoms/Input";
import { Label } from "@admin/components/atoms/Input/Label";
import { Modal, ModalProps } from "@admin/components/atoms/Modal";
import {
  CheckboxStateValue,
  MultiCheckbox,
} from "@admin/components/atoms/MultiStateCheckbox/MultiCheckbox";
import {
  addMaintenanceExam,
  answerMulticheckValues,
  FoodControlPointInstance,
  InstanceStatus,
  MaintenanceExam,
  updateMaintenanceExam,
  useAuthStore,
} from "@enos-monorepo/lib";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface YearlyMaintenanceModalProps extends ModalProps {
  foodControlPointInstance: FoodControlPointInstance;
  initialData?: MaintenanceExam;
}

export function YearlyMaintenanceModal({
  foodControlPointInstance,
  initialData,
  ...props
}: YearlyMaintenanceModalProps) {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [isCorrectiveActionNeeded, setIsCorrectiveActionNeeded] =
    useState(false);
  const [isCorrectiveMessage, setIsCorrectiveMessage] = useState(false);
  const [checkBoxStates, setCheckBoxStates] = useState<CheckboxStateValue[]>(
    [],
  );
  const addMaintenanceControlSchema = z.object({
    remarks: z.string().optional(),
    correctiveActionsMessage: z
      .string()
      .optional()
      .refine(
        (e) => {
          if (isCorrectiveActionNeeded && isCorrectiveMessage) return true;
          if (!isCorrectiveActionNeeded) return true;
          return false;
        },
        {
          message: t("food.At leat one corrective action needed"),
        },
      ),
  });
  type AddMaintenanceControl = z.infer<typeof addMaintenanceControlSchema>;
  function processValidationNumbers(checkBoxStates: CheckboxStateValue[]) {
    const valueToNumberMap: { [key: string]: number } = {
      yes: 1,
      partial: 2,
      no: 3,
    };

    const result: {
      ValidateThermometer: number;
      ValidateDoorAndWindow: number;
      ValidateCleaning: number;
      ValidatePests: number;
      ValidateWallAndFloor: number;
      ValidateSewers: number;
    } = {
      ValidateThermometer: 1,
      ValidateDoorAndWindow: 1,
      ValidateCleaning: 1,
      ValidatePests: 1,
      ValidateWallAndFloor: 1,
      ValidateSewers: 1,
    };

    checkBoxStates.forEach((state) => {
      const numberValue = valueToNumberMap[state.value as string];
      result[state.stateName as keyof typeof result] = numberValue;
    });
    return result;
  }

  const checkBoxData: CheckboxStateValue[] = useMemo(
    () => [
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateThermometer)
          : "yes",
        stateName: "ValidateThermometer",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateDoorAndWindow)
          : "yes",
        stateName: "ValidateDoorAndWindow",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateCleaning)
          : "yes",
        stateName: "ValidateCleaning",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validatePests)
          : "yes",
        stateName: "ValidatePests",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateWallAndFloor)
          : "yes",
        stateName: "ValidateWallAndFloor",
      },
      {
        value: initialData
          ? answerMulticheckValues(initialData.validateSewers)
          : "yes",
        stateName: "ValidateSewers",
      },
    ],
    [initialData],
  );

  const checkBoxTexts: string[] = [
    t("food.Food Temperature Mesaurement"),
    t("food.Windows Control"),
    t("food.Sewers Control"),
    t("food.Pest Control Yearly"),
    t("food.Rust Free"),
    t("food.Washable"),
  ];

  const onSubmit = (data: AddMaintenanceControl) => {
    if (!user?.id) return;

    const validationNumbers = processValidationNumbers(checkBoxStates);

    const isCompeletedWithErrors = isCorrectiveActionNeeded;

    const payload = {
      companyId: foodControlPointInstance.companyId,
      companyLocationId: foodControlPointInstance.companyLocationId,
      remark: data.remarks,
      reason: data.correctiveActionsMessage,
      controlPointInstanceId: foodControlPointInstance.id,
      foodControlPointInstance: foodControlPointInstance,
      status: isCompeletedWithErrors
        ? InstanceStatus.completedWithErrors
        : InstanceStatus.completed,
      validateThermometer: validationNumbers.ValidateThermometer,
      validateDoorAndWindow: validationNumbers.ValidateDoorAndWindow,
      validateCleaning: validationNumbers.ValidateSewers,
      validatePests: validationNumbers.ValidatePests,
      validateWallAndFloor: validationNumbers.ValidateWallAndFloor,
      validateSewers: validationNumbers.ValidateSewers,
      registeredBy: user?.id,
    };

    if (initialData) {
      updateMutation.mutate(payload);
    } else {
      mutation.mutate(payload);
    }
  };

  const queryClient = useQueryClient();
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<AddMaintenanceControl>({
    resolver: zodResolver(addMaintenanceControlSchema),
  });

  const updateMutation = useMutation({
    mutationFn: updateMaintenanceExam,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      setIsCorrectiveActionNeeded(false);
      setIsCorrectiveMessage(false);
      setCheckBoxStates(checkBoxData);
      props.onClose?.();
      toast.success(t("food.Maintenance Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.An error occurred while adding Maintenance Control"));
    },
  });
  const mutation = useMutation({
    mutationFn: addMaintenanceExam,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPoints",
          foodControlPointInstance.companyLocationId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          "foodControlPointInstances",
          foodControlPointInstance?.companyLocationId,
        ],
      });
      reset();
      setIsCorrectiveActionNeeded(false);
      setIsCorrectiveMessage(false);
      setCheckBoxStates(checkBoxData);
      props.onClose?.();
      toast.success(t("food.Maintenance Control Has Been Added"), {
        duration: 4000,
      });
    },
    onError: (err: Error) => {
      console.error("Mutation error:", err.message);
      toast.error(t("food.An error occurred while adding Maintenance Control"));
    },
  });

  const handleAction = (e: CheckboxStateValue) => {
    setCheckBoxStates((prevState) => {
      const newState = prevState.map((state) =>
        state.stateName === e.stateName ? { ...state, value: e.value } : state,
      );

      const isCorrectiveNeeded = newState.some(
        (state) => state.value === "partial" || state.value === "no",
      );

      setIsCorrectiveActionNeeded(isCorrectiveNeeded);

      return newState;
    });
  };

  useEffect(() => {
    if (checkBoxStates.length === 0) setCheckBoxStates(checkBoxData);
    if (initialData) {
      const { remark, reason } = initialData.note
        ? JSON.parse(initialData.note)
        : "";
      setIsCorrectiveActionNeeded(
        checkBoxStates.some(
          (state) => state.value === "partial" || state.value === "no",
        ),
      );
      reset({
        remarks: remark,
        correctiveActionsMessage: reason,
      });
    }
  }, [checkBoxData, checkBoxStates, checkBoxStates.length, initialData, reset]);

  return (
    <Modal
      actionButtons={
        <>
          <Button variant="secondary" onClick={props?.onClose}>
            {t("common.Discard")}
          </Button>
          <Button loading={mutation.isPending} onClick={handleSubmit(onSubmit)}>
            {t("food.Submit Entry")}
          </Button>
        </>
      }
      {...props}
      title={t("food.Maintenance Examination")}
    >
      <form>
        <div className="flex flex-col gap-y-6">
          {checkBoxTexts.map((text, index) => {
            return (
              <div
                key={checkBoxData[index].stateName + "_" + index}
                className="flex flex-row items-center gap-x-3"
              >
                <label className="form-label !font-normal">{text}</label>
                <div className="flex flex-col gap-2">
                  <MultiCheckbox
                    stateName={checkBoxData[index].stateName}
                    onChange={(e) => handleAction(e)}
                    value={
                      checkBoxStates.find(
                        (state) =>
                          state.stateName === checkBoxData[index].stateName,
                      )?.value
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-b border-b-gray-200 my-2.5"></div>
        {isCorrectiveActionNeeded && (
          <div className="flex flex-col gap-2">
            <Label title={t("food.Corrective Action")} required>
              <Input
                showRequired={!isCorrectiveMessage}
                {...register("correctiveActionsMessage")}
                onChange={(e) => setIsCorrectiveMessage(!!e.target.value)}
              />
            </Label>
            <ErrorMessage message={errors?.correctiveActionsMessage?.message} />
          </div>
        )}
        <Label title={t("food.Remarks")}>
          <div className="flex flex-col items-end w-full gap-2 ">
            <Input {...register("remarks")} placeholder="-" />
            <ErrorMessage message={errors?.remarks?.message} />
          </div>
        </Label>
      </form>
    </Modal>
  );
}
